// src/screens/LandingScreen.jsx
// Ruta sugerida: /asesoria  o  /contacto
// Tráfico de redes sociales → conversión directa

import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import ProspectForm from "../components/ProspectForm";

/* ======================================================
   SOCIAL PROOF
====================================================== */
const PRUEBAS = [
  { num: "10+",  label: "años de experiencia" },
  { num: "100%", label: "en línea" },
  { num: "3",    label: "idiomas: ES · EN · FR" },
];

const PARA_QUIEN = [
  "Freelancers y creativos",
  "Doctores y nutriólogos",
  "Entrenadores y coaches",
  "Desarrolladores y tech workers",
  "Arquitectos e ingenieros",
  "Cualquier persona con ingresos propios",
];

/* ======================================================
   SCREEN
====================================================== */
const LandingScreen = () => {
  const formRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-white text-neutral-900">
      <Helmet>
        <title>Agenda tu asesoría contable gratuita — SolCont México</title>
        <meta name="description" content="Contabilidad digital para freelancers, PFAE y RESICO en todo México. Agenda una asesoría gratuita con un contador certificado." />
        <meta name="robots" content="noindex,follow" />
      </Helmet>

      {/* ======================================================
          HERO — máxima tensión, mínimo ruido
      ====================================================== */}
      <section className="relative flex min-h-[92vh] flex-col items-start justify-center overflow-hidden bg-black px-6 py-20 text-white sm:px-10 md:px-16 lg:px-24">

        {/* Textura de fondo sutil */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }}
        />

        {/* Glow ambiental */}
        <div
          className="pointer-events-none absolute -right-40 top-0 h-[500px] w-[500px] rounded-full opacity-10"
          style={{ background: "radial-gradient(circle, #ffffff 0%, transparent 70%)" }}
        />

        {/* Logo mínimo arriba */}
        

        {/* Contenido hero */}
        <div className="relative z-10 max-w-2xl">
          {/* Eyebrow */}
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/8 px-4 py-1.5 backdrop-blur-sm">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white/70" />
            <span className="text-xs font-medium tracking-wide text-white/80">
              Asesoría gratuita · sin compromisos
            </span>
          </div>

          <h1 className="text-4xl font-extralight leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Tu contador digital,<br />
            <span className="font-semibold">en todo México.</span>
          </h1>

          <p className="mt-6 text-base leading-relaxed text-white/70 sm:text-lg">
            Si generas ingresos propios, tienes obligaciones fiscales.
            Nosotros las manejamos por ti — sin que tengas que entender de contabilidad.
          </p>

          {/* Stats */}
          <div className="mt-10 flex flex-wrap gap-8">
            {PRUEBAS.map(p => (
              <div key={p.label}>
                <p className="text-2xl font-semibold text-white">{p.num}</p>
                <p className="mt-0.5 text-xs text-white/50">{p.label}</p>
              </div>
            ))}
          </div>

          {/* CTA principal */}
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={scrollToForm}
              className="rounded-lg bg-white px-7 py-3.5 text-sm font-semibold text-neutral-900 shadow-md transition hover:bg-neutral-100 active:scale-95"
            >
              Agendar asesoría gratuita
            </button>
            <Link
              to="/"
              className="text-sm text-white/50 transition hover:text-white"
            >
              Conocer más →
            </Link>
          </div>
        </div>

        {/* Flecha scroll */}
        <button
          onClick={scrollToForm}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/30 transition hover:text-white/60"
          aria-label="Ir al formulario"
        >
          <span className="text-xs tracking-widest">CONTINUAR</span>
          <svg className="h-4 w-4 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </section>

      {/* ======================================================
          PARA QUIÉN — franja de identificación
      ====================================================== */}
      <section className="border-b border-neutral-100 bg-neutral-50 py-12 px-6 sm:px-10">
        <div className="mx-auto max-w-4xl">
          <p className="mb-6 text-xs font-semibold uppercase tracking-widest text-neutral-400">
            ¿Es esto para mí?
          </p>
          <div className="flex flex-wrap gap-3">
            {PARA_QUIEN.map(item => (
              <span
                key={item}
                className="rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-700"
              >
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ======================================================
          QUÉ INCLUYE — 3 puntos clave antes del form
      ====================================================== */}
      <section className="mx-auto max-w-4xl px-6 py-16 sm:px-10">
        <div className="grid gap-px border border-neutral-100 bg-neutral-100 sm:grid-cols-3">
          {[
            {
              num:  "01",
              title: "Agenda en 2 minutos",
              desc:  "Llena el formulario y un contador te contacta en menos de 24 horas hábiles.",
            },
            {
              num:  "02",
              title: "Asesoría por videollamada",
              desc:  "Revisamos tu situación fiscal actual sin que salgas de casa.",
            },
            {
              num:  "03",
              title: "Sin compromisos",
              desc:  "La asesoría es gratuita. Si decides continuar, te presentamos un plan claro.",
            },
          ].map(item => (
            <div key={item.num} className="bg-white px-7 py-8">
              <p className="text-xs font-semibold text-neutral-300">{item.num}</p>
              <h3 className="mt-3 text-base font-semibold text-neutral-900">{item.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-500">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ======================================================
          FORMULARIO — sección principal de conversión
      ====================================================== */}
      <section
        ref={formRef}
        id="formulario"
        className="border-t border-neutral-100 bg-neutral-50 px-6 py-16 sm:px-10"
      >
        <div className="mx-auto max-w-2xl">
          {/* Encabezado de la sección */}
          <div className="mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-neutral-400">
              Paso 1 de 1
            </p>
            <h2 className="mt-3 text-2xl font-semibold leading-snug text-neutral-900 sm:text-3xl">
              Agenda tu asesoría gratuita
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-neutral-500">
              Tarda menos de 2 minutos. Un contador certificado te contactará en las próximas 24 horas hábiles.
            </p>
          </div>

          {/* Formulario reutilizado */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm">
            <ProspectForm />
          </div>
        </div>
      </section>

      {/* ======================================================
          FOOTER MÍNIMO — solo lo necesario para dar confianza
      ====================================================== */}
      <footer className="border-t border-neutral-100 px-6 py-8 sm:px-10">
        <div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center sm:flex-row sm:justify-between sm:text-left">
          <div className="flex items-center gap-3">
            <img
              src={`${process.env.PUBLIC_URL}/media/solcontlogowhite.png`}
              alt="SolCont México"
              className="h-5 w-auto object-contain opacity-40"
              style={{ filter: "invert(1)" }}
            />
            <span className="text-xs text-neutral-400">
              © {new Date().getFullYear()} SolCont México
            </span>
          </div>

          <div className="flex items-center gap-5 text-xs text-neutral-400">
            <Link to="/privacidad" className="transition hover:text-neutral-700">
              Aviso de Privacidad
            </Link>
            <Link to="/terms" className="transition hover:text-neutral-700">
              Términos
            </Link>
            <Link to="/" className="font-medium text-neutral-600 transition hover:text-neutral-900">
              Ir al sitio →
            </Link>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingScreen;